/* eslint-disable @next/next/no-img-element */
import React, { useEffect } from "react";
import { definitions } from "../../types/supabase";
import { randomUUID } from "crypto";
import CCWebinarRegistrationModal1 from "../../components/CCWebinarRegistrationModal/CCWebinarRegistrationModal1";
import { apiUrl } from "../../utils/apiServer/apiUrl";
import { TrackingWrapper } from "../../components/trackEvent";
import { PageContext } from "../../components/cms/PageContext";
import { CustomCssVars } from "../../components/CustomCssVars";
import { NoCohortIdentifier, fetchComputedFlags } from "@/utils/featureFlags";
import { SetFeatureFlags } from "@/types/decisions";
import { CMSSchedule } from "components/cms/types/cmsTypes";
import { FeatureFlagContext } from "components/FeatureFlagContext";
import prisma from "@/utils/prisma/prisma";

const WebinarRegistrationModal = ({
  brand,
  brandConfiguration,
  clientSideId,
  schedule,
  redirectUrl,
  webinarBrandPersonaImage,
  webinarBrandPersonaName,
  webinarPageId,
  featureFlags,
  featureFlagHash,
  webinarId,
  thankYouPageUrl,
  webinarUrl,
}: {
  brand: definitions["brands"];
  brandConfiguration: definitions["brand_configurations"];
  clientSideId: string;
  schedule: CMSSchedule;
  webinarBrandPersonaImage: definitions["resources"];
  webinarBrandPersonaName: definitions["resources"];
  redirectUrl: string;
  webinarPageId: string;
  featureFlags: SetFeatureFlags;
  featureFlagHash: string;
  webinarId: string;
  thankYouPageUrl?: string;
  webinarUrl?: string;
}) => {
  const trackEvent = (eventName: string, eventProperties: any) => {
    eventProperties = eventProperties || {};

    window.parent.postMessage(
      JSON.stringify({ type: eventName, ...eventProperties }),
      "*"
    );
  };

  const hideModal = () => {
    window.parent.postMessage(
      JSON.stringify({
        type: "close-modal",
      }),
      "*"
    );
  };

  const fakeResource = (value: string) => {
    return {
      type: "text" as "text",
      id: "fake",
      metadata: {
        value: value,
      },
    };
  };

  useEffect(() => {
    // this lets the injector script know that React is working and ready to receive users via the injected iFrame on LPs
    trackEvent("webinar-registration-experience-initialized", {});
  }, []);

  return (
    <>
      <PageContext.Provider
        value={{ emitEvent: () => {}, addEventListener: () => {} }}
      >
        <CustomCssVars brandConfig={brandConfiguration} />
        <TrackingWrapper>
          <FeatureFlagContext.Provider
            value={{
              featureFlags,
              evaluatedFeatureFlagsHash: featureFlagHash,
            }}
          >
            <CCWebinarRegistrationModal1
              thankYouPageUrl={thankYouPageUrl}
              webinarUrl={webinarUrl}
              useNewRegistration={true}
              webinarId={webinarId}
              featureFlags={featureFlags}
              featureFlagHash={featureFlagHash}
              handleRedirect={(payload) =>
                trackEvent("redirect", { type: "redirect", payload })
              }
              injected={true}
              hideModal={hideModal}
              apiUrl={apiUrl}
              brand={brand}
              brandAbbreviation={brand.abbreviation as "piaf" | "nma"}
              brandConfig={brandConfiguration}
              buttonText={fakeResource("Get Started")}
              clientSideId={clientSideId}
              component={{} as any}
              isDevelopmentMode={false}
              redirectToUrl={fakeResource(redirectUrl)}
              registrationHeadline={fakeResource("Register for Webinar")}
              schedule={schedule as any}
              stripePublishableKey=""
              webinarBrandPersonaImage={webinarBrandPersonaImage as any}
              webinarBrandPersonaName={webinarBrandPersonaName as any}
              webinarPageId={fakeResource(webinarPageId)}
            />
          </FeatureFlagContext.Provider>
        </TrackingWrapper>
      </PageContext.Provider>
    </>
  );
};

// This gets called on every request
export async function getServerSideProps(context: any) {
  const { query } = context;
  const { webinarId, thankYouPageUrl, webinarUrl } = query;

  const webinar = await prisma.webinar.findFirstOrThrow({
    where: {
      id: webinarId,
    },
    include: {
      brand: {
        include: {
          brandConfigurations: true,
        },
      },
    },
  });

  const brandAbbreviation = webinar.brand.abbreviation as
    | "piaf"
    | "nma"
    | "da"
    | "btb";
  const resourceIds = {
    piaf: {
      webinarBrandPersonaName: "54d2d08a-c0eb-4d04-981d-4ee0595cad1f",
      webinarBrandPersonaImage: "3e0a18c7-3c32-4fa5-be07-32b22d97c039",
    },
    nma: {
      webinarBrandPersonaName: "0d667095-8a8d-41f7-a85a-80594ea6fec4",
      webinarBrandPersonaImage: "89931b3f-10b8-4d7b-af2a-57d0af1841b2",
    },
    da: {
      webinarBrandPersonaName: "9f2c28ee-9f49-49a8-af42-5abc55c31323",
      webinarBrandPersonaImage: "15d79cf6-4893-4767-8cb8-a0bf6cfdb290",
    },
    btb: {
      webinarBrandPersonaName: "b3879c74-f150-472c-8b9f-c456e188acc9",
      webinarBrandPersonaImage: "f0e2d69e-0482-4ecd-b048-8b77743305a4",
    },
  } as const;

  const fetchedResources = await prisma.resource.findMany({
    where: {
      id: {
        in: [
          webinar.scheduleResourceId,
          resourceIds[brandAbbreviation].webinarBrandPersonaName,
          resourceIds[brandAbbreviation].webinarBrandPersonaImage,
        ],
      },
    },
  });

  const webinarBrandPersonaName =
    fetchedResources.find(
      (resource) =>
        resource.id === resourceIds[brandAbbreviation].webinarBrandPersonaName
    ) || null;
  const webinarBrandPersonaImage =
    fetchedResources.find(
      (resource) =>
        resource.id === resourceIds[brandAbbreviation].webinarBrandPersonaImage
    ) || null;

  const schedule = fetchedResources.find(
    (resource) => resource.id === webinar.scheduleResourceId
  ) as unknown as CMSSchedule;

  if (!webinar.brand) {
    throw new Error("Brand is required.");
  }

  const { featureFlags, evaluatedFeatureFlagsHash } =
    await fetchComputedFlags(NoCohortIdentifier);

  return {
    props: {
      brand: webinar.brand,
      brandConfiguration: webinar.brand.brandConfigurations[0],
      clientSideId: randomUUID(),
      schedule: schedule,
      webinarId,
      webinarBrandPersonaImage: webinarBrandPersonaImage,
      webinarBrandPersonaName: webinarBrandPersonaName,
      featureFlags: featureFlags,
      featureFlagHash: evaluatedFeatureFlagsHash,
      thankYouPageUrl: thankYouPageUrl || null,
      webinarUrl: webinarUrl || null,
    },
  };
}

export default WebinarRegistrationModal;
